import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';

interface PriorityListModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (isNHL: boolean) => void;
  playerName: string;
  language: 'en' | 'fr';
  message?: string | null;
  messageSuccess?: boolean;
  onAcknowledge?: () => void;
}

export function PriorityListModal({ 
  show, 
  onHide, 
  onConfirm, 
  playerName, 
  language 
  , message = null, messageSuccess = false, onAcknowledge
}: PriorityListModalProps) {
  const [isNHL, setIsNHL] = useState<boolean>(true); // Par défaut, club principal

   const modalRef = useRef<HTMLDivElement>(null); // 👈 on crée une référence vers le contenu
  
    useEffect(() => {
      if (show && modalRef.current) {
        // 👇 On force le scroll vers l’élément
        modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [show]);

  if (!show) return null;

  const titleText = language === 'fr' 
    ? 'Ajouter à la Liste Prioritaire' 
    : 'Add to Priority List';
  
  const confirmText = language === 'fr'
    ? 'Confirmer l\'ajout'
    : 'Confirm Addition';
    
  const cancelText = language === 'fr'
    ? 'Annuler'
    : 'Cancel';
    
  const messageText = language === 'fr'
    ? `Voulez-vous ajouter ${playerName} à votre liste de sélection automatique?`
    : `Do you want to add ${playerName} to your automatic selection list?`;
    
  const infoText = language === 'fr'
    ? 'Les joueurs sur cette liste seront automatiquement sélectionnés si votre temps expire pendant votre tour de repêchage.'
    : 'Players on this list will be automatically selected if your time expires during your draft turn.';

  const teamSelectionText = language === 'fr'
    ? 'Assigner ce joueur à:'
    : 'Assign this player to:';

  const mainClubText = language === 'fr'
    ? 'Club Principal'
    : 'Main Club';

  const reservesText = language === 'fr'
    ? 'Équipe Réserve'
    : 'Reserve Team';

  return (
    <>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }} 
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center">
                <Star size={20} className="me-2 text-warning" />
                {titleText}
              </h5>
              {!message && <button type="button" className="btn-close" aria-label="Fermer" onClick={onHide}></button>}
            </div>
            <div className="modal-body">
              {!message ? (
                <>
                  <p>{messageText}</p>
                  <div className="form-group mb-3">
                    <label className="form-label">{teamSelectionText}</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="teamAssignment"
                        id="mainClub"
                        checked={isNHL}
                        onChange={() => setIsNHL(true)}
                      />
                      <label className="form-check-label" htmlFor="mainClub">
                        {mainClubText}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="teamAssignment"
                        id="reserves"
                        checked={!isNHL}
                        onChange={() => setIsNHL(false)}
                      />
                      <label className="form-check-label" htmlFor="reserves">
                        {reservesText}
                      </label>
                    </div>
                  </div>
                  <div className="alert alert-warning">
                    <small>{infoText}</small>
                  </div>
                </>
              ) : (
                <div role="alert" aria-live="polite" className={`alert ${messageSuccess ? 'alert-success' : 'alert-danger'}`}>
                  {message}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {message ? (
                <div className="d-grid w-100">
                  <button className="btn btn-primary" onClick={onAcknowledge}>
                    {language === 'fr' ? "J'ai compris" : 'OK'}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onHide}
                  >
                    {cancelText}
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning d-flex align-items-center"
                    onClick={() => onConfirm(isNHL)}
                  >
                    <Star size={16} className="me-2" />
                    {confirmText}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
      ></div>
    </>
  );
}